'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

export function PricingSection({ isAuthenticated = false }: { isAuthenticated?: boolean }) {
  const t = useTranslations('landing.pricing');

  const plans = [
    {
      code: 'pay_as_you_go',
      name: t('payAsYouGo.name'),
      monthlyPrice: null,
      meteredLabel: '$0.03',
      meteredUnit: '/min',
      minutesLabel: null,
      description: t('payAsYouGo.description'),
      features: [
        t('payAsYouGo.feature1'),
        t('payAsYouGo.feature2'),
        t('payAsYouGo.feature3'),
        t('payAsYouGo.feature4'),
      ],
      cta: t('getStarted'),
      highlight: false,
    },
    {
      code: 'enterprise_1',
      name: t('starter.name'),
      monthlyPrice: 59,
      description: t('starter.description'),
      meteredLabel: null,
      meteredUnit: null,
      minutesLabel: t('starter.minutesLabel'),
      features: [
        t('starter.feature1'),
        t('starter.feature2'),
        t('starter.feature3'),
        t('starter.feature4'),
        t('starter.feature5'),
      ],
      cta: t('startFreeTrial'),
      highlight: true,
    },
    {
      code: 'enterprise_5',
      name: t('growth.name'),
      monthlyPrice: 249,
      description: t('growth.description'),
      meteredLabel: null,
      meteredUnit: null,
      minutesLabel: t('growth.minutesLabel'),
      features: [
        t('growth.feature1'),
        t('growth.feature2'),
        t('growth.feature3'),
        t('growth.feature4'),
        t('growth.feature5'),
      ],
      cta: t('startFreeTrial'),
      highlight: false,
    },
  ];

  const getPlanHref = (code: string) => {
    const params = new URLSearchParams({ plan: code, cadence: 'monthly' });
    if (isAuthenticated) return `/upgrade?${params.toString()}`;
    params.set('mode', 'signup');
    params.set('redirect', '/onboarding');
    return `/login?${params.toString()}`;
  };

  return (
    <section
      id="pricing"
      data-theme="dark"
      className="py-24 relative overflow-hidden bg-black rounded-t-[2rem] -mt-8 z-[55]"
    >
      <div className="container max-w-[1100px] mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4 }}
            className="text-3xl md:text-5xl font-serif text-white mb-4"
          >
            {t('title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4, delay: 0.08 }}
            className="text-base text-white/50 max-w-md mx-auto"
          >
            {t('subtitle')}
          </motion.p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-4 items-stretch">
          {plans.map((plan, idx) => {
            const price = plan.meteredLabel ? plan.meteredLabel : `$${plan.monthlyPrice}`;

            const unit = plan.meteredUnit ? plan.meteredUnit : '/mo';

            return (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                className="relative flex flex-col"
              >
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 bg-primary text-white text-[10px] font-semibold px-3 py-0.5 rounded-full tracking-wider uppercase">
                    {t('popular')}
                  </div>
                )}

                <div
                  className={`relative flex flex-col flex-1 rounded-2xl border p-6 transition-all duration-300 ${
                    plan.highlight
                      ? 'bg-white/[0.05] border-primary/40'
                      : 'bg-white/[0.02] border-white/8 hover:border-white/15'
                  }`}
                >
                  {/* Plan name */}
                  <p
                    className={`text-xs font-semibold uppercase tracking-widest mb-5 ${plan.highlight ? 'text-primary' : 'text-white/40'}`}
                  >
                    {plan.name}
                  </p>

                  {/* Price */}
                  <div className="flex items-baseline gap-0.5 mb-1">
                    <span className="text-4xl font-serif text-white">{price}</span>
                    <span className="text-sm text-white/40">{unit}</span>
                  </div>

                  {/* Billing note */}
                  {plan.minutesLabel && (
                    <p className="text-[11px] text-white/30">
                      {t('includedMinutes', { minutes: plan.minutesLabel })}
                    </p>
                  )}

                  <p className="text-sm text-white/50 mt-3 mb-6 leading-relaxed">
                    {plan.description}
                  </p>

                  <div
                    className={`h-px w-full mb-5 ${plan.highlight ? 'bg-primary/15' : 'bg-white/6'}`}
                  />

                  {/* Features */}
                  <ul className="flex flex-col gap-2.5 mb-8 flex-1">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2.5">
                        <Check className="w-3.5 h-3.5 shrink-0 text-primary/70" strokeWidth={2.5} />
                        <span className="text-[13px] text-white/60">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Link
                    href={getPlanHref(plan.code)}
                    className={`inline-flex h-10 items-center justify-center rounded-full text-sm font-medium transition-all duration-300 ${
                      plan.highlight
                        ? 'bg-primary text-white hover:brightness-110'
                        : 'border border-white/15 text-white/80 hover:bg-white/5'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Individual / Candidate Plan */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-8 rounded-2xl bg-white/[0.02] border border-white/8 p-6 md:p-8 flex flex-col md:flex-row items-center gap-8"
        >
          <div className="flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-white/30 mb-1.5">
              {t('individual.title')}
            </p>
            <h3 className="text-xl font-serif text-white mb-1.5">{t('individual.name')}</h3>
            <p className="text-sm text-white/45 mb-4">{t('individual.description')}</p>
            <div className="flex items-baseline gap-1 mb-5">
              <span className="text-3xl font-serif text-white">$14</span>
              <span className="text-sm text-white/40">/month</span>
            </div>
            <Link
              href={getPlanHref('individual')}
              className="inline-flex h-9 items-center justify-center rounded-full bg-primary px-6 text-sm font-medium text-white hover:brightness-110 hover:scale-105 transition-all duration-300"
            >
              {t('individual.getStarted')}
            </Link>
          </div>

          <div className="flex-1 w-full grid grid-cols-2 gap-x-6 gap-y-2.5">
            {[
              t('individual.feature1'),
              t('individual.feature2'),
              t('individual.feature3'),
              t('individual.feature4'),
            ].map((f) => (
              <div key={f} className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-white/25 shrink-0" strokeWidth={2.5} />
                <span className="text-[13px] text-white/50">{f}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Bottom note */}
        <p className="text-center text-xs text-white/30 mt-8">{t('bottomNote')}</p>
      </div>
    </section>
  );
}
